import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Zap, Shield, Gauge, Activity } from "lucide-react";

// Modificamos FloatingCard para que acepte customStyle y fuerce la posición
const FloatingCard = ({ className, children, delay = 0, rotate = 0, x = 0, y = 0, customStyle = {}, float = {} }) => {
  const {
    yRange = [0, -8, 0],
    rotateRange = [rotate, rotate + 3, rotate - 2, rotate],
    duration = 6,
  } = float;

  return (
    <motion.div
      initial={{ opacity: 0, y: 40, rotate: rotate - 4 }}
      animate={{
        opacity: 1,
        y: yRange,
        rotate: rotateRange,
      }}
      transition={{
        opacity: { duration: 0.8, delay, ease: [0.22, 0.8, 0.25, 1] },
        y: { duration, delay, repeat: Infinity, ease: "easeInOut" },
        rotate: { duration: duration * 1.2, delay, repeat: Infinity, ease: "easeInOut" },
      }}
      whileHover={{ y: -4, scale: 1.02 }}
      className={`absolute rounded-2xl p-4 backdrop-blur-xl osx-gradient-border ${className}`}
      style={{
        background: "color-mix(in oklab, var(--osx-surface) 70%, transparent)",
        border: "1px solid var(--osx-border-strong)",
        boxShadow: "var(--osx-shadow-2)",
        transform: `translate(${x}px, ${y}px) rotate(${rotate}deg)`,
        ...customStyle
      }}
    >
      {children}
    </motion.div>
  );
};

export const HeroScene = () => {
  return (
    <section
      className="relative overflow-hidden osx-noise"
      style={{ background: "var(--osx-bg)" }}
      data-testid="hero-section"
    >
      <div className="absolute inset-0 osx-grid-bg opacity-60" aria-hidden />
      <div className="osx-orbs" aria-hidden>
        <div className="osx-orb osx-orb--1" />
        <div className="osx-orb osx-orb--2" />
        <div className="osx-orb osx-orb--3" />
      </div>

      <div className="relative osx-container pt-16 sm:pt-24 lg:pt-28 pb-20 lg:pb-28 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold mb-6"
            style={{
              background: "var(--osx-surface-2)",
              border: "1px solid var(--osx-border-strong)",
              color: "var(--osx-brand-600)",
            }}
          >
            <span className="relative inline-flex h-2 w-2">
              <span className="absolute inset-0 rounded-full animate-ping" style={{ background: "var(--osx-accent-cyan)", opacity: 0.7 }} />
              <span className="relative inline-flex h-2 w-2 rounded-full" style={{ background: "var(--osx-brand-600)" }} />
            </span>
            Infraestructura Bare Metal · Ryzen 9 · NVMe
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="text-4xl sm:text-5xl lg:text-7xl font-bold leading-[1.02] tracking-tight"
            style={{ color: "var(--osx-text)" }}
            data-testid="hero-headline"
          >
            Potencia. Estabilidad.{" "}
            <span className="osx-gradient-text">Control Absoluto.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.25 }}
            className="mt-6 text-base sm:text-lg lg:text-xl max-w-xl leading-relaxed"
            style={{ color: "var(--osx-text-muted)" }}
            data-testid="hero-subcopy"
          >
            Hosting premium para <strong style={{ color: "var(--osx-text)" }}>Minecraft</strong>, <strong style={{ color: "var(--osx-text)" }}>Game Servers</strong>, <strong style={{ color: "var(--osx-text)" }}>VPS</strong> y <strong style={{ color: "var(--osx-text)" }}>Servidores Dedicados</strong>. Desplegamos tu servidor en segundos con protección Anti-DDoS, panel Pterodactyl y soporte 24/7.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-8 flex flex-wrap items-center gap-3"
          >
            <a
              href="#pricing"
              data-testid="hero-primary-cta"
              className="group inline-flex items-center gap-2 px-6 py-3.5 rounded-xl text-sm sm:text-base font-semibold text-white"
              style={{
                background: "linear-gradient(135deg, var(--osx-brand-600), var(--osx-accent-cyan))",
                boxShadow: "0 12px 30px rgba(2,132,199,0.35)",
                transition: "transform 0.2s ease, box-shadow 0.2s ease",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; }}
            >
              Ver planes
              <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
            </a>
            <a
              href="https://tienda.optishieldx.com/"
              target="_blank"
              rel="noreferrer noopener"
              data-testid="hero-secondary-cta"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl text-sm sm:text-base font-semibold"
              style={{
                background: "var(--osx-surface)",
                border: "1px solid var(--osx-border-strong)",
                color: "var(--osx-text)",
                transition: "background-color 0.2s ease, border-color 0.2s ease",
              }}
            >
              Contratar ahora
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.55 }}
            className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-xl"
          >
            {[
              { icon: Activity, label: "Uptime", value: "99.8%" },
              { icon: Zap, label: "Latencia", value: "< 65ms" },
              { icon: Shield, label: "Anti-DDoS", value: "Incluido" },
              { icon: Gauge, label: "NVMe", value: "Gen4" },
            ].map((item) => (
              <div key={item.label} className="flex flex-col gap-1">
                <div className="inline-flex items-center gap-1.5 text-xs font-medium" style={{ color: "var(--osx-text-muted)" }}>
                  <item.icon size={12} />
                  {item.label}
                </div>
                <div className="text-xl sm:text-2xl font-bold font-mono" style={{ color: "var(--osx-text)" }}>
                  {item.value}
                </div>
              </div>
            ))}
          </motion.div>
        </div>

        {/* Lado derecho: Tarjetas 3D flotantes */}
        <div className="lg:col-span-5 relative h-[380px] sm:h-[460px] hidden md:block" aria-hidden>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1 }}
            className="absolute inset-0 flex items-center justify-center"
          >
            {/* Núcleo central (R9) */}
            <motion.div
              className="relative w-60 h-60 rounded-[28px] z-20"
              style={{
                background:
                  "radial-gradient(circle at 30% 20%, rgba(56,189,248,0.9), rgba(2,132,199,0.8) 60%, rgba(2,132,199,0.6))",
                boxShadow:
                  "0 0 80px rgba(56,189,248,0.45), 0 30px 80px rgba(2,132,199,0.35), inset 0 0 60px rgba(255,255,255,0.1)",
              }}
              animate={{ rotate: [0, 6, -4, 0], y: [0, -10, 0] }}
              transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
            >
              <div
                className="absolute inset-3 rounded-[22px] flex flex-col items-center justify-center text-white"
                style={{
                  background: "linear-gradient(135deg, rgba(7,24,39,0.9), rgba(7,24,39,0.6))",
                  border: "1px solid rgba(255,255,255,0.18)",
                  backdropFilter: "blur(10px)",
                }}
              >
                <span className="text-[10px] font-mono uppercase tracking-[0.25em] text-cyan-300/90">bare metal</span>
                <div className="text-3xl font-bold mt-2 font-mono">R9</div>
                <div className="text-xs mt-1 text-cyan-200/80">Ryzen 9 5900X</div>
                <div className="mt-3 flex items-center gap-1.5">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  <span className="text-[10px] text-emerald-300 font-mono">ONLINE</span>
                </div>
              </div>
            </motion.div>
          </motion.div>

          {/* Tarjeta Latency (Arriba a la Izquierda) */}
          <FloatingCard 
            className="w-48 z-40" 
            customStyle={{ top: '8%', left: '5%' }} 
            delay={0.4} 
            rotate={-6}
            float={{ yRange: [0, -10, 2, 0], rotateRange: [-6, -3, -8, -6], duration: 7 }}
          >
            <div className="text-[10px] font-mono uppercase tracking-widest" style={{ color: "var(--osx-text-muted)" }}>Latency</div>
            <div className="text-2xl font-bold font-mono mt-1" style={{ color: "var(--osx-brand-600)" }}>64ms</div>
            <div className="mt-2 flex items-end gap-1 h-8">
              {[30,55,40,70,60,85,72,90,65].map((h,i) => (
                <div key={`latency-${i}-${h}`} className="w-1.5 rounded-sm" style={{ height: `${h}%`, background: "linear-gradient(180deg, var(--osx-brand-400), var(--osx-brand-600))" }} />
              ))}
            </div>
          </FloatingCard>

          {/* Tarjeta Pterodactyl — ESQUINA INFERIOR IZQUIERDA */}
          <FloatingCard 
            className="w-44 z-30" 
            customStyle={{ bottom: '-200px', left: '-12px' }} 
            delay={0.8} 
            rotate={-3}
            float={{ yRange: [0, 8, -3, 0], rotateRange: [-3, -1, -5, -3], duration: 8 }}
          >
            <div className="text-[10px] font-mono uppercase tracking-widest" style={{ color: "var(--osx-text-muted)" }}>Pterodactyl</div>
            <div className="mt-1 flex items-baseline gap-2">
              <span className="text-xl font-bold" style={{ color: "var(--osx-text)" }}>Panel</span>
              <span className="text-[10px] font-mono" style={{ color: "var(--osx-brand-600)" }}>v1.12</span>
            </div>
            <div className="mt-2 flex gap-1">
              {[1,2,3,4,5,6].map((i) => (
                <div key={`panel-indicator-${i}`} className="h-1.5 w-4 rounded-full" style={{ background: i <= 5 ? "var(--osx-brand-400)" : "var(--osx-border-strong)" }} />
              ))}
            </div>
          </FloatingCard>

          {/* Tarjeta Anti-DDoS — CENTRO VERTICAL del contenedor */}
          <FloatingCard 
            className="w-52 z-30" 
            customStyle={{ top: 'calc(50% - 270px)', left: 'calc(50% + 90px)' }} 
            delay={0.6} 
            rotate={8}
            float={{ yRange: [0, -7, 4, 0], rotateRange: [8, 11, 5, 8], duration: 9 }}
          >
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-lg flex items-center justify-center" style={{ background: "var(--osx-surface-2)", border: "1px solid var(--osx-border-strong)" }}>
                <Shield size={14} style={{ color: "var(--osx-brand-600)" }} />
              </div>
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest" style={{ color: "var(--osx-text-muted)" }}>Anti-DDoS</div>
                <div className="text-sm font-bold" style={{ color: "var(--osx-text)" }}>Active</div>
              </div>
            </div>
            <div className="mt-2 text-[11px]" style={{ color: "var(--osx-text-muted)" }}>
              Mitigación 100 Tbps · Protección a nivel de red y aplicación
            </div>
          </FloatingCard>
        </div>
      </div>
    </section>
  );
};